import videojs from 'video.js'
import { revealElement, hideElement, registerPluginOnce } from '../utils.js'

const Plugin = videojs.getPlugin('plugin')

// Default options for the plugin.
const defaults = {
  message: null
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBRatings extends Plugin {
  /**
   * Create a NFBRatings plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.options = videojs.obj.merge(defaults, options)

    this.init = this.init.bind(this)
    this.showRatings = this.showRatings.bind(this)
    this.hideRatings = this.hideRatings.bind(this)

    this.isVisible = true
    this.hasNfbOverlay = false
    this.controlPrefix = `${this.player.id()}-`
    this.playerEl = this.player.el()
    this.els = {
      container: null
    }

    this.player.on('ready', this.init)
  }

  async init () {
    this.log('Initializing NFB Ratings plugin')

    if (this.options.dynamicCssImport) {
      import('../sass/plugins/ratings.sass')
    }

    try {
      if (this.options.message) {
        this.player.addClass('vjs-nfb-ratings')
        this.hasNfbOverlay = this.player.hasPlugin('nfbOverlay')
        this.addRatingsMessage()
      }
    } catch (e) {
      this.log(e.message)
    }
  }

  addRatingsMessage () {
    this.createContainer()
    this.els.container.innerHTML = `<div class="rating-message">${this.options.message}</div>`
  }

  createContainer () {
    this.log('Creating rating container')

    let container = this.playerEl
    if (this.hasNfbOverlay) {
      container = this.player.nfbOverlay().els.leftControlsSecondRowContainer
    } else {
      this.addListeners()
    }

    this.els.container = container.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.container, 'ratings-container')
  }

  addListeners () {
    this.player.on('useractive', this.showRatings)
    this.player.on('userinactive', this.hideRatings)
    this.player.on('ended', this.showRatings)
  }

  showRatings () {
    if (!this.isVisible) {
      this.log('Show ratings')
      revealElement(this.els.container, { slideDown: true })
      this.isVisible = true
    }
  }

  hideRatings () {
    const nfbTriggerWarningsPlugin = this.player.nfbTriggerWarnings && this.player.nfbTriggerWarnings()
    if (!this.player.paused() || (nfbTriggerWarningsPlugin && nfbTriggerWarningsPlugin.isShowing)) {
      this.log('Hide ratings')
      hideElement(this.els.container, { slideUp: true })
      this.isVisible = false
    }
  }

  assignControlAttributes (element, controlName) {
    element.id = this.controlPrefix + controlName
    element.className = `${this.controlPrefix + controlName} ${controlName}`
  }
}

// Define default values for the plugin's `state` object here.
NFBRatings.defaultState = {}

// Include the version number.
NFBRatings.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbRatings', NFBRatings)

export default NFBRatings
